import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getSubject, updateSubject, togglePin, moveToKept, returnToActive } from "@/lib/api";
import { Heart, Sparkles, ArrowLeft, PenLine, Pin, Archive, MoonStar } from "lucide-react";

function fmtDate(iso) {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

export default function PersonThreadPage() {
    const { id } = useParams();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [answering, setAnswering] = useState(false);
    const [answeredNote, setAnsweredNote] = useState("");
    const [showStoryPrompt, setShowStoryPrompt] = useState(false);
    const [showKeptDialog, setShowKeptDialog] = useState(false);
    const [pinError, setPinError] = useState("");

    const load = () => getSubject(id).then(setData);
    useEffect(() => { load(); }, [id]);

    if (!data) return <div className="p-6 font-ui text-sm text-ink-40">Loading…</div>;
    const s = data.subject;
    const isKept = s.shelf === "kept";

    const markAnswered = async () => {
        await updateSubject(s.id, { status: "answered", answered_note: answeredNote.trim() || null });
        setAnswering(false);
        setShowStoryPrompt(true);
        load();
    };
    const doPin = async () => {
        setPinError("");
        try { await togglePin(s.id); load(); }
        catch (err) { setPinError(err?.response?.data?.detail || "Could not pin."); }
    };
    const doMoveKept = async () => {
        await moveToKept(s.id);
        setShowKeptDialog(false);
        load();
    };
    const doReturnActive = async () => {
        await returnToActive(s.id);
        load();
    };

    const openStory = () => {
        nav(`/journal/new?mentionSubject=${s.id}&mentionName=${encodeURIComponent(s.name)}&suggestCategory=Answered`);
    };

    const restart = async () => {
        await updateSubject(s.id, { status: "praying" });
        load();
    };

    return (
        <div className="px-5 md:px-8 py-4 md:py-6 pb-32 mx-auto w-full" style={{ maxWidth: "68ch" }}>
            <button onClick={() => nav("/prayer")} className="inline-flex items-center gap-1.5 font-ui text-[13px] text-ink-70 hover:text-ink mb-3">
                <ArrowLeft size={14} /> Praying-for list
            </button>

            <div className="p-5 rounded-3xl bg-white border border-ink-10 mb-5">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h1 data-testid="prayer-name" className="scripture text-3xl text-ink tracking-tight">{s.name}</h1>
                        <div className="mt-1 font-ui text-[12px] text-ink-40">
                            {isKept
                                ? <>On the shelf · {s.shelf_reason === "answered" ? "Answered" : s.shelf_reason === "laid_down" ? "Laid down" : "Kept"}</>
                                : s.status === "answered"
                                    ? <>Answered {s.answered_at ? `on ${new Date(s.answered_at).toLocaleDateString()}` : ""}</>
                                    : s.status === "laid_down"
                                        ? <>Laid down {s.laid_down_at ? `on ${new Date(s.laid_down_at).toLocaleDateString()}` : ""}</>
                                        : <>Praying since {new Date(s.started_at).toLocaleDateString()}</>}
                        </div>
                        {s.answered_note && (
                            <div data-testid="answered-note" className="mt-2 font-ui text-[13px] text-moss-deep italic">“{s.answered_note}”</div>
                        )}
                        {s.laid_down_note && (
                            <div data-testid="laid-down-note" className="mt-2 font-ui text-[13px] text-slate-600 italic">“{s.laid_down_note}”</div>
                        )}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                        {!isKept && (
                            <button
                                data-testid="thread-pin-toggle"
                                onClick={doPin}
                                aria-pressed={!!s.pinned}
                                aria-label={s.pinned ? "Unpin" : "Pin"}
                                className={
                                    "w-9 h-9 rounded-full flex items-center justify-center transition " +
                                    (s.pinned ? "bg-plum text-white" : "text-ink-40 hover:text-plum hover:bg-plum/10")
                                }
                            >
                                <Pin size={14} />
                            </button>
                        )}
                        {isKept ? (
                            <span data-testid="status-chip" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-plum/10 text-plum font-ui text-[11px] font-semibold uppercase tracking-[0.14em]">
                                <Archive size={12} /> Kept
                            </span>
                        ) : s.status === "answered" ? (
                            <span data-testid="status-chip" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-moss/10 text-moss-deep font-ui text-[11px] font-semibold uppercase tracking-[0.14em]">
                                <Sparkles size={12} /> Answered
                            </span>
                        ) : s.status === "laid_down" ? (
                            <span data-testid="status-chip" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 font-ui text-[11px] font-semibold uppercase tracking-[0.14em]">
                                <MoonStar size={12} /> Laid down
                            </span>
                        ) : (
                            <span data-testid="status-chip" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-plum/10 text-plum font-ui text-[11px] font-semibold uppercase tracking-[0.14em]">
                                <Heart size={12} /> Praying
                            </span>
                        )}
                    </div>
                </div>

                {pinError && (
                    <div data-testid="pin-error" className="mt-3 p-2 rounded-lg bg-plum/5 border border-plum/20 font-ui text-[12px] text-plum">
                        {pinError}
                    </div>
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                    {isKept ? (
                        <button
                            data-testid="return-to-active"
                            onClick={doReturnActive}
                            className="px-3 py-1.5 rounded-full bg-plum text-white font-ui text-[12px] hover:opacity-90 transition"
                        >
                            Return to active list
                        </button>
                    ) : (
                        <>
                            {s.status === "praying" && (
                                <>
                                    {!answering ? (
                                        <button data-testid="mark-answered" onClick={() => setAnswering(true)}
                                            className="px-3 py-1.5 rounded-full bg-moss text-white font-ui text-[12px] hover:bg-moss-deep transition">
                                            Mark answered
                                        </button>
                                    ) : (
                                        <div className="flex flex-wrap items-center gap-2 w-full">
                                            <input
                                                data-testid="answered-note-input"
                                                autoFocus
                                                value={answeredNote}
                                                onChange={(e) => setAnsweredNote(e.target.value)}
                                                placeholder="Optional note — e.g. Came to faith July 12"
                                                className="flex-1 min-w-[220px] px-3 py-2 rounded-full bg-white border border-moss/40 font-ui text-[13px] outline-none focus:ring-2 focus:ring-moss/20"
                                            />
                                            <button data-testid="save-answered" onClick={markAnswered} className="px-3 py-1.5 rounded-full bg-moss text-white font-ui text-[12px]">Save</button>
                                            <button onClick={() => setAnswering(false)} className="px-3 py-1.5 rounded-full font-ui text-[12px] text-ink-70">Cancel</button>
                                        </div>
                                    )}
                                </>
                            )}
                            {s.status === "answered" && (
                                <button data-testid="mark-praying" onClick={restart}
                                    className="px-3 py-1.5 rounded-full bg-plum text-white font-ui text-[12px] hover:opacity-90 transition">
                                    Return to praying
                                </button>
                            )}
                            {s.status === "laid_down" && (
                                <button data-testid="mark-praying" onClick={restart}
                                    className="px-3 py-1.5 rounded-full bg-plum text-white font-ui text-[12px] hover:opacity-90 transition">
                                    Return to praying
                                </button>
                            )}
                            <button
                                data-testid="open-kept-dialog"
                                onClick={() => setShowKeptDialog(true)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-ink-10 font-ui text-[12px] text-ink hover:border-plum/40 transition"
                            >
                                <Archive size={12} /> Move to Kept
                            </button>
                        </>
                    )}
                    <button
                        data-testid="new-entry-for-person"
                        onClick={openStory}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-ink-10 font-ui text-[12px] text-ink hover:border-moss/40 transition"
                    >
                        <PenLine size={12} /> New entry
                    </button>
                </div>
            </div>

            {showKeptDialog && (
                <div data-testid="kept-dialog" className="fixed inset-0 z-50 bg-ink/40 flex items-end lg:items-center justify-center p-4">
                    <div className="w-full max-w-md bg-white rounded-3xl p-6 shadow-2xl animate-sheet-in">
                        <h3 className="scripture text-xl text-ink mb-2">Move {s.name} to Kept?</h3>
                        <p className="scripture text-[15px] text-ink-70 leading-relaxed">
                            Their whole story stays — you can bring them back anytime.
                        </p>
                        <div className="mt-5 flex justify-end gap-2">
                            <button onClick={() => setShowKeptDialog(false)} className="px-4 py-2 rounded-full font-ui text-[13px] text-ink-70">Cancel</button>
                            <button data-testid="confirm-move-kept" onClick={doMoveKept} className="px-4 py-2 rounded-full bg-plum text-white font-ui text-[13px]">Move to Kept</button>
                        </div>
                    </div>
                </div>
            )}

            {showStoryPrompt && (
                <div data-testid="story-prompt" className="mb-4 p-4 rounded-2xl bg-moss/5 border border-moss/20 flex items-center justify-between gap-3">
                    <div className="font-ui text-[13px] text-ink-70">Would you like to write the story?</div>
                    <div className="flex gap-2">
                        <button data-testid="story-prompt-write" onClick={openStory} className="px-3 py-1.5 rounded-full bg-moss text-white font-ui text-[12px]">Write it</button>
                        <button data-testid="story-prompt-dismiss" onClick={() => setShowStoryPrompt(false)} className="px-3 py-1.5 rounded-full font-ui text-[12px] text-ink-70">Not now</button>
                    </div>
                </div>
            )}

            <h2 className="scripture text-xl text-ink mt-2 mb-2">Thread</h2>
            {data.thread.length === 0 ? (
                <p data-testid="empty-thread" className="scripture text-[16px] text-ink-70">
                    Your entries mentioning {s.name} will gather here.
                </p>
            ) : (
                <ul className="space-y-2">
                    {data.thread.map(e => {
                        const firstMention = (e.content || []).find(n => n.type === "mention" && n.subject_id === s.id);
                        return (
                            <li key={e.id}>
                                <button
                                    data-testid={`thread-entry-${e.id}`}
                                    onClick={() => nav(`/journal/${e.id}?from=person&subject=${s.id}${firstMention ? `&node=${firstMention.node_id}` : ""}`)}
                                    className="w-full text-left p-4 rounded-2xl bg-white border border-ink-10 hover:border-plum/40 hover:shadow-md transition"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="font-ui text-[11px] uppercase tracking-[0.2em] text-plum font-semibold">
                                            {fmtDate(e.date)}
                                        </div>
                                        {e.mention_count > 1 && (
                                            <span className="font-ui text-[10px] text-ink-40">mentioned {e.mention_count}×</span>
                                        )}
                                    </div>
                                    <p className="scripture text-[15px] text-ink leading-snug line-clamp-3 mt-1">
                                        {e.excerpt}
                                    </p>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
